import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowUpRight, PenLine, UserRound } from "lucide-react";
import { Seo, breadcrumbLd, faqLd } from "@/components/site/Seo";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { RichText } from "@/components/site/RichText";
import { PostBody } from "@/components/site/PostBody";
import { CtaBand } from "@/components/site/CtaBand";
import { useContent, useItem, SITE_URL } from "@/lib/api";
import NotFound from "./NotFound";

// Dates stay in structured data and meta tags only; they are intentionally not shown on the page.
const dateOf = (p) => p.published_at || p.created_at;
const AUTHOR_BIO = "Freya Jones is the founder of Digital AdDoctor, bringing over 18 years of experience in digital marketing, PPC, SEO, analytics and conversion optimisation. With a background in psychology and behavioural science, Freya combines data-driven strategies with an understanding of customer behaviour to help businesses grow.";

const Meta = ({ p, dark }) => (
  <p className={`flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-semibold ${dark ? "text-violet-200" : "text-slate-500"}`}>
    <span className="inline-flex items-center gap-1.5"><UserRound className="h-3.5 w-3.5" aria-hidden />{p.author || "Freya Jones"}</span>
  </p>
);

export const PostCard = ({ p, i = 0 }) => (
  <Reveal delay={(i % 3) * 0.06} className="h-full">
    <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="group flex h-full flex-col overflow-hidden rounded-[28px] bg-white transition-[transform,box-shadow] duration-500 hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(46,16,101,.35)]">
      <div className="aspect-[16/9] w-full overflow-hidden bg-gradient-to-br from-[#581C87] to-[#2563EB]">
        {p.image && <img src={p.image} alt={p.image_alt || p.title} loading="lazy" decoding="async" width="1600" height="900" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />}
      </div>
      <div className="flex flex-1 flex-col p-7">
        <span className="text-xs font-bold uppercase tracking-widest text-[#7C3AED]">{p.category}</span>
        <h2 className="mt-3 text-xl font-extrabold leading-snug text-[#13052A]">{p.title}</h2>
        <p className="mt-3 text-sm leading-relaxed text-slate-600">{p.excerpt}</p>
        <div className="mt-auto pt-6"><Meta p={p} /></div>
        <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-[#581C87]">Read article <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" /></span>
      </div>
    </Link>
  </Reveal>
);

const Empty = () => (
  <Reveal className="mx-auto max-w-xl rounded-[28px] bg-white p-10 text-center">
    <PenLine className="mx-auto h-8 w-8 text-[#7C3AED]" />
    <h2 className="mt-4 text-2xl font-extrabold text-[#13052A]">New articles are on the way</h2>
    <p className="mt-3 text-slate-600">In the meantime, a free audit is the quickest way to get tailored advice.</p>
    <Link to="/contact?type=audit" className="btn-primary mt-6" data-testid="blog-empty-audit">Get a Free Digital Audit</Link>
  </Reveal>
);

// Topic filters group CMS categories. Unknown or new categories fall into Business Growth so no article is ever hidden.
const TOPICS = [
  ["seo", "SEO & Local Search", ["SEO", "SEO & Local Search", "GEO & AI Search"]],
  ["google-ads", "Google Ads", ["Google Ads"]],
  ["social-ads", "Facebook & Instagram Ads", ["Paid Social", "Meta Ads", "Facebook & Instagram Ads"]],
  ["leads", "Leads & Conversions", ["Conversion Rate Optimisation", "Lead Generation", "Analytics & Tracking"]],
  ["maths", "Marketing Maths & Costs", ["Marketing Maths", "Pricing"]],
  ["growth", "Business Growth", []],
];
const topicOf = (p) => (TOPICS.find(([, , cats]) => cats.includes(p.category)) || TOPICS[TOPICS.length - 1])[0];

const TopicFilters = ({ items, active, onPick }) => {
  const count = (key) => (key === "all" ? items.length : items.filter((p) => topicOf(p) === key).length);
  const opts = [["all", "All articles"], ...TOPICS.map(([k, l]) => [k, l])].filter(([k]) => count(k) > 0);
  return (
    <div className="-mx-5 mb-10 overflow-x-auto px-5 sm:mx-0 sm:px-0" data-testid="blog-filters">
      <div role="group" aria-label="Filter articles by topic" className="flex w-max gap-2 sm:w-auto sm:flex-wrap">
        {opts.map(([k, l]) => {
          const on = active === k;
          return (
            <button key={k} type="button" aria-pressed={on} onClick={() => onPick(k)} data-testid={`blog-filter-${k}`}
              className={`inline-flex shrink-0 items-center gap-2 rounded-full border px-4 py-2.5 text-sm font-bold transition-colors duration-200 ${on ? "border-[#581C87] bg-[#581C87] text-white" : "border-violet-200 bg-white text-[#13052A] hover:border-[#7C3AED] hover:text-[#581C87]"}`}>
              {l}<span className={`rounded-full px-2 py-0.5 text-xs ${on ? "bg-white/20 text-white" : "bg-[#F5F0FF] text-[#581C87]"}`}>{count(k)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export function Blog() {
  const { items, loading } = useContent("posts");
  const [params, setParams] = useSearchParams();
  const topic = TOPICS.some(([k]) => k === params.get("topic")) ? params.get("topic") : "all";
  const shown = topic === "all" ? items : items.filter((p) => topicOf(p) === topic);
  const pick = (k) => setParams(k === "all" ? {} : { topic: k }, { replace: true });
  const crumbs = [["Home", "/"], ["Blog", "/blog"]];
  return (
    <>
      <Seo title="Digital Marketing Blog | SEO & PPC Advice, Tunbridge Wells" path="/blog"
        description="Practical SEO, Google Ads, PPC and digital marketing advice for Tunbridge Wells and Kent businesses, from Freya Jones, founder of Digital AdDoctor."
        jsonLd={[{ "@type": "Blog", name: "Digital AdDoctor Blog", url: `${SITE_URL}/blog`, publisher: { "@id": `${SITE_URL}/#business` },
          blogPost: items.map((p) => ({ "@type": "BlogPosting", headline: p.title, url: `${SITE_URL}/blog/${p.slug}`, datePublished: dateOf(p) })) }, breadcrumbLd(crumbs)]} />
      <PageHero crumbs={crumbs} eyebrow="Blog & insights · Tunbridge Wells" lines={["Straight-talking", "marketing advice."]}
        intro="Practical guides on SEO, Google Ads, paid social and websites, written for Tunbridge Wells and Kent business owners, not marketers." />
      <section className="bg-[#F5F0FF] py-20 sm:py-28" data-testid="blog-list">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          {!loading && items.length === 0 && <Empty />}
          {items.length > 0 && <TopicFilters items={items} active={topic} onPick={pick} />}
          <p className="sr-only" aria-live="polite" data-testid="blog-filter-count">{shown.length} articles shown</p>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {shown.map((p, i) => <PostCard key={p.slug} p={p} i={i} />)}
          </div>
        </div>
      </section>
      <CtaBand title="Let's Grow Your Business" text="Want advice tailored to your business? Book a free discovery call or request a free digital audit." />
    </>
  );
}

const AuthorBox = ({ p }) => (
  <aside className="mt-14 flex flex-col gap-6 rounded-[28px] bg-[#F5F0FF] p-7 sm:flex-row sm:p-8" data-testid="blog-author">
    <img src="/img/freya-86.jpg" alt="Freya Jones, founder of Digital AdDoctor" loading="lazy" width="96" height="96" className="h-24 w-24 shrink-0 rounded-full object-cover object-top" />
    <div>
      <p className="text-xs font-bold uppercase tracking-widest text-[#7C3AED]">Written by</p>
      <p className="mt-2 text-lg font-extrabold text-[#13052A]">{p.author || "Freya Jones"}, Founder, Digital AdDoctor</p>
      <p className="mt-3 text-sm leading-relaxed text-slate-700">{p.author_bio || AUTHOR_BIO}</p>
      <Link to="/about-freya-jones" className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-[#581C87] hover:text-[#13052A]" data-testid="blog-author-link">More about Freya Jones <ArrowUpRight className="h-4 w-4" /></Link>
    </div>
  </aside>
);

const PostCta = ({ p }) => (
  <div className="mt-10 rounded-[28px] bg-[#13052A] p-8 text-white sm:p-10" data-testid="blog-post-cta">
    <p className="text-2xl font-extrabold">{p.cta_heading || "Ready to take the next step?"}</p>
    {p.cta_note && <p className="mt-3 text-base text-violet-100">{p.cta_note}</p>}
    <div className="mt-6 flex flex-wrap gap-3">
      <Link to={p.cta_to || (p.related_service ? `/${p.related_service}` : "/contact")} className="btn-accent" data-testid="blog-post-cta-primary">{p.cta_label || "Explore the related service"} <ArrowUpRight className="h-4 w-4" /></Link>
      <Link to="/contact?type=call" className="btn-ghost-dark" data-testid="blog-post-cta-contact">Book a Digital Marketing Consultation</Link>
    </div>
  </div>
);

const Related = ({ slug }) => {
  const { items } = useContent("posts");
  const me = items.find((x) => x.slug === slug) || {};
  const tags = new Set([me.related_service, ...(me.link_pages || [])]);
  const score = (x) => [x.related_service, ...(x.link_pages || [])].filter((t) => tags.has(t)).length;
  const others = items.filter((x) => x.slug !== slug).sort((a, b) => score(b) - score(a)).slice(0, 3);
  if (!others.length) return null;
  return (
    <section className="bg-[#F5F0FF] py-20 sm:py-24" data-testid="blog-related">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <h2 className="text-2xl font-extrabold text-[#13052A] sm:text-3xl">Related articles</h2>
        <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3">{others.map((x, i) => <PostCard key={x.slug} p={x} i={i} />)}</div>
      </div>
    </section>
  );
};

const articleLd = (p) => ({
  "@type": "BlogPosting", headline: p.meta_title || p.title, description: p.meta_description || p.excerpt,
  image: p.image ? [p.image.startsWith("/") ? `${SITE_URL}${p.image}` : p.image] : undefined, datePublished: dateOf(p), dateModified: p.updated_at || dateOf(p),
  author: { "@type": "Person", "@id": `${SITE_URL}/#freya`, name: p.author || "Freya Jones", url: `${SITE_URL}/about-freya-jones` },
  publisher: { "@id": `${SITE_URL}/#business` }, mainEntityOfPage: { "@type": "WebPage", "@id": p.canonical || `${SITE_URL}/blog/${p.slug}` },
  articleSection: p.category, inLanguage: "en-GB", ...(p.focus_keyword ? { keywords: p.focus_keyword } : {}),
});

// FAQ structured data comes from the article's own "Frequently asked questions" section (h3 question + p answer).
const strip = (s) => s.replace(/<[^>]+>/g, "").replace(/&amp;/g, "&").replace(/&#39;|&rsquo;/g, "'").replace(/&quot;/g, '"').trim();
const faqsFrom = (html = "") => {
  const i = html.search(/<h2[^>]*>\s*Frequently asked questions/i);
  if (i < 0) return [];
  const rest = html.slice(i + 3);
  const section = rest.slice(0, rest.search(/<h2/i) >= 0 ? rest.search(/<h2/i) : undefined);
  return [...section.matchAll(/<h3[^>]*>([\s\S]*?)<\/h3>\s*<p[^>]*>([\s\S]*?)<\/p>/gi)].map((m) => ({ q: strip(m[1]), a: strip(m[2]) }));
};

export function PostView({ p, preview = false }) {
  const crumbs = [["Home", "/"], ["Blog", "/blog"], [p.title, `/blog/${p.slug}`]];
  const ogImage = p.og_image || p.image;
  const faqs = faqsFrom(p.body_html);
  return (
    <>
      <Seo raw title={p.meta_title || p.title} description={p.meta_description || p.excerpt} path={`/blog/${p.slug}`} type="article" noindex={preview}
        canonical={p.canonical || undefined} ogTitle={p.og_title || undefined}
        image={ogImage && (ogImage.startsWith("/") || /^https?:/.test(ogImage)) ? ogImage : undefined} imageAlt={p.image_alt} jsonLd={[articleLd(p), breadcrumbLd(crumbs), ...(faqs.length ? [faqLd(faqs)] : [])]}>
        <meta property="article:published_time" content={dateOf(p)} />
        <meta property="article:author" content={`${SITE_URL}/about-freya-jones`} />
      </Seo>
      <PageHero crumbs={crumbs} eyebrow={p.category} lines={[p.title]} intro={p.excerpt}>
        <Meta p={p} dark />
      </PageHero>
      <article className="bg-white py-16 sm:py-20" data-testid="blog-article">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          {p.image && <img src={p.image} alt={p.image_alt || p.title} width="1600" height="900" fetchPriority="high" className="mb-12 aspect-[16/9] w-full rounded-[28px] object-cover" data-testid="blog-article-image" />}
          {p.body_html !== undefined ? <PostBody html={p.body_html} /> : <RichText text={p.body} />}
          <PostCta p={p} />
          <AuthorBox p={p} />
        </div>
      </article>
      {!preview && <Related slug={p.slug} />}
      <CtaBand title={p.band_title || "Let's Grow Your Business"} text={p.band_text || "Talk to Freya about SEO, Google Ads and digital marketing for your Tunbridge Wells business."}>
        {p.band_button && <Link to={p.band_to || "/contact"} className="btn-accent" data-testid="blog-band-cta">{p.band_button} <ArrowUpRight className="h-4 w-4" /></Link>}
      </CtaBand>
    </>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const { item: p, loading, error } = useItem("posts", slug);
  if (error) return <NotFound />;
  if (loading || !p) return <div className="min-h-screen bg-[#13052A]" />;
  if (p.redirect_to) return <Navigate to={`/blog/${p.redirect_to}`} replace />;
  return <PostView p={p} />;
}
